import {
    startTransition,
    useContext,
    useEffect,
    useState,
    type FC
} from "react";
import type IChartTableToggleProps from "./type";
import {
    ChartTableToggleContext
} from "./context";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    ChartColumn as ChartIcon,
    Table2 as TableIcon
} from "lucide-react-native";
import {
    ChartContext
} from "../../context";
import AnimatedIcon from "../../../animatedIcon";
import Tooltip from "../../../tooltip";
import Button from "../../../button";

const ChartTableToggleIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const isTableVisible = useContext(ChartTableToggleContext);

    return <AnimatedIcon
        isTransitioned={isTableVisible}
        customColor={customColor}
        iconTransition="none"
        fromIcon={TableIcon}
        toIcon={ChartIcon}
        color={color}
        size={size}
    />;
};

const ChartTableToggle: FC<IChartTableToggleProps> = ({
    customLocalize: customLocalizeProp,
    isTableVisible: isTableVisibleProp,
    customTheme: customThemeProp,
    isDisabled: isDisabledProp,
    onToggle,
    ...props
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const isOn = isTableVisibleProp ?? context?.isTableVisible ?? false;

    const [
        pendingValue,
        setPendingValue
    ] = useState<boolean | undefined>(undefined);

    const isIconOn = pendingValue ?? isOn;

    useEffect(() => {
        setPendingValue(undefined);
    }, [
        isOn
    ]);

    const isDisabled = isDisabledProp ?? context?.isTableDisabled ?? false;

    const title = localize(isIconOn ? "chart-show-as-chart" : "chart-show-as-table");

    const handlePress = () => {
        const nextValue = !isIconOn;

        setPendingValue(nextValue);

        startTransition(() => {
            if(onToggle) {
                onToggle(nextValue);

                return;
            }

            context?.toggleTable();
        });
    };

    return <ChartTableToggleContext.Provider
        value={isIconOn}
    >
        <Tooltip
            customLocalize={customLocalizeProp ?? context?.customLocalize}
            customTheme={customTheme}
            title={title}
        >
            <Button
                {...props}
                aria-controls={context ? `${context.baseID}-table` : undefined}
                icon={ChartTableToggleIcon}
                customTheme={customTheme}
                isDisabled={isDisabled}
                spreadBehaviour="free"
                onPress={handlePress}
                aria-label={title}
                variant="outline"
                type="neutral"
                size="small"
            />
        </Tooltip>
    </ChartTableToggleContext.Provider>;
};
export default ChartTableToggle;
